import { CalendarDotsIcon, CaretLeftIcon, CaretRightIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';

import { Breadcrumb } from '../../../../../components/Breadcrumb';
import { Button } from '../../../../../components/button/Button';
import { PageHeader } from '../../../../../components/PageHeader';
import { Input } from '../../../../../components/input/Input';
import { OrderSelect } from './OrderSelect';
import { CreateInvoiceProvider, useCreateInvoiceCtx } from './context';
import { CustomerSelect } from './CustomerSelect';
import { InvoiceReview } from './Review';
import { PageHeading } from 'components/PageHeading';
import { ToggleGroup } from 'components/toggle-group/ToggleGroup';

type InvoiceFrequencyFilter = 'ALL' | 'TRANSPORT' | 'WEEKLY' | 'MONTHLY';

const INVOICE_FREQUENCY_FILTER_OPTIONS: { value: InvoiceFrequencyFilter; label: string }[] = [
  { value: 'ALL', label: 'Alle' },
  { value: 'TRANSPORT', label: 'Per transport' },
  { value: 'WEEKLY', label: 'Wekelijks' },
  { value: 'MONTHLY', label: 'Maandelijks' },
];

const CreateInvoiceComponent = () => {
  const [searchValue, setSearchValue] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [invoiceFrequencyFilter, setInvoiceFrequencyFilter] = useState<InvoiceFrequencyFilter>('ALL');
  const { state: invoiceState, dispatch } = useCreateInvoiceCtx();

  useEffect(() => {
    setSearchValue('');
  }, [invoiceState.selectedCustomer]);

  let searchText = 'search';
  switch (invoiceState.activeWindow) {
    case 'SELECT_CUSTOMER':
      searchText = 'Zoek een klant';
      break;
    case 'SELECT_ORDERS':
      searchText = 'Zoek order(s)';
      break;
  }

  let canGoForward = false;
  if (invoiceState.activeWindow === 'SELECT_CUSTOMER') {
    canGoForward = invoiceState.selectedCustomer !== null;
  } else if (invoiceState.activeWindow === 'SELECT_ORDERS') {
    canGoForward = invoiceState.orders.length > 0;
  }

  return (
    <>
      <PageHeader title="Nieuwe verkoop" />

      <PageHeading
        leftSide={
          <Breadcrumb
            parentItem={{
              name: 'Verkopen',
              to: '/internal/finance/sales',
            }}
            currentItem="Nieuwe verkoop"
          />
        }
      />

      <div className="px-4">
        <div className="flex justify-between mb-4">
          <h2 className="heading-two self-start">
            {invoiceState.selectedCustomer ? 'Selecteer order(s)' : 'Selecteer een klant'}
          </h2>

          <div className="flex gap-4">
            <div>
              <Button
                iconLeft={<CaretLeftIcon className="button-icon" />}
                isDisabled={invoiceState.activeWindow === 'SELECT_CUSTOMER'}
                onTrigger={() => {
                  dispatch({
                    type: 'GO_BACK',
                  });
                }}
              >
                Keer terug
              </Button>
            </div>

            <div>
              <Button
                color="primary"
                iconRight={<CaretRightIcon className="button-icon" />}
                isDisabled={!canGoForward}
                onTrigger={() => {
                  dispatch({
                    type: 'GO_FORWARD',
                  });
                }}
              >
                Ga verder
              </Button>
            </div>
          </div>
        </div>

        {invoiceState.activeWindow !== 'REVIEW' && (
          <div className="mb-4 flex gap-4">
            <div className="flex-1">
              <Input
                type="text"
                placeholder={searchText}
                value={searchValue}
                onChange={setSearchValue}
                iconLeft={<MagnifyingGlassIcon className="input-icon" />}
              />
            </div>

            {invoiceState.activeWindow === 'SELECT_CUSTOMER' && (
              <>
                <div className="w-48">
                  <Input
                    type="date"
                    placeholder="Losdatum van"
                    value={dateFrom}
                    onChange={setDateFrom}
                    iconLeft={<CalendarDotsIcon className="input-icon" />}
                  />
                </div>
                <div className="w-48">
                  <Input
                    type="date"
                    placeholder="Losdatum tot"
                    value={dateTo}
                    onChange={setDateTo}
                    iconLeft={<CalendarDotsIcon className="input-icon" />}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {invoiceState.activeWindow === 'SELECT_CUSTOMER' && (
          <div className="mb-4">
            <div className="label-text mb-2">Facturatiefrequentie</div>
            <ToggleGroup
              options={INVOICE_FREQUENCY_FILTER_OPTIONS}
              value={invoiceFrequencyFilter}
              onChange={setInvoiceFrequencyFilter}
            />
          </div>
        )}

        {invoiceState.activeWindow === 'SELECT_CUSTOMER' && (
          <CustomerSelect
            searchValue={searchValue}
            dateFrom={dateFrom}
            dateTo={dateTo}
            invoiceFrequency={invoiceFrequencyFilter === 'ALL' ? undefined : invoiceFrequencyFilter}
          />
        )}
        {invoiceState.activeWindow === 'SELECT_ORDERS' && <OrderSelect searchValue={searchValue} />}
        {invoiceState.activeWindow === 'REVIEW' && <InvoiceReview />}
      </div>
    </>
  );
};

const CreateInvoicePage = () => {
  return (
    <CreateInvoiceProvider>
      <CreateInvoiceComponent />
    </CreateInvoiceProvider>
  );
};

export default CreateInvoicePage;
